/**
 * The editor (via splat-transform) applies a 180 degree rotation about z when
 * loading gaussian splat PLY files (INRIA convention) - verified empirically:
 * imported splats get an entity rotation of (x:0, y:0, z:1, w:0).
 *
 * Shapes are authored in editor world space (y-up), so data destined for a
 * PLY file - or for a GSplatData paired with the standard load rotation -
 * must be pre-rotated by the inverse. All current shapes are symmetric under
 * this rotation, but color gradients or point-defined shapes would silently
 * flip without it.
 */

import { SplatBuffers } from './types';

// quaternion of the rotation the loader applies (w, x, y, z) = (0, 0, 0, 1)
const PLY_LOAD_ROTATION = { x: 0, y: 0, z: 1, w: 0 };

// convert authored world-space buffers to PLY file convention, in place:
// positions rotate 180 degrees about z, quaternions are pre-multiplied by the
// inverse load rotation: (w, x, y, z) -> (z, y, -x, -w)
const toPlyConvention = (buffers: SplatBuffers): SplatBuffers => {
    const { count, x, y, rot_0, rot_1, rot_2, rot_3 } = buffers;

    for (let i = 0; i < count; i++) {
        x[i] = -x[i];
        y[i] = -y[i];

        const w = rot_0[i];
        const qx = rot_1[i];
        const qy = rot_2[i];
        const qz = rot_3[i];
        rot_0[i] = qz;
        rot_1[i] = qy;
        rot_2[i] = -qx;
        rot_3[i] = -w;
    }

    return buffers;
};

export { toPlyConvention, PLY_LOAD_ROTATION };
